import { FormEvent, useState } from 'react';
import { api } from '../lib/api';
import { FormField } from './FormField';
import type { User, FamilyRole, CustodyPatternType, DayOfWeek } from '@kinapp/shared';

interface CustodySetupProps {
  familyId: string;
  dependentId: string;
  familyMembers: Array<{ user: User; role: FamilyRole }>;
  onSuccess: () => void;
  onCancel: () => void;
}

const DAYS: DayOfWeek[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'] as DayOfWeek[];
const DAY_LABELS: Record<string, string> = {
  SUNDAY: 'Sun', MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed',
  THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat',
};

export function CustodySetup({ familyId, dependentId, familyMembers, onSuccess, onCancel }: CustodySetupProps) {
  const [patternType, setPatternType] = useState<CustodyPatternType>('ALTERNATING' as CustodyPatternType);
  const [parentA, setParentA] = useState(familyMembers[0]?.user.id || '');
  const [parentB, setParentB] = useState(familyMembers[1]?.user.id || familyMembers[0]?.user.id || '');
  const [weeklyAssignments, setWeeklyAssignments] = useState<Record<string, string>>(
    Object.fromEntries(DAYS.map((d) => [d, familyMembers[0]?.user.id || '']))
  );
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [handoverTime, setHandoverTime] = useState('18:00');
  const [handoverLocation, setHandoverLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let pattern: any;

      switch (patternType) {
        case 'WEEKLY':
          pattern = { type: 'WEEKLY', assignments: weeklyAssignments };
          break;
        case 'ALTERNATING':
          pattern = { type: 'ALTERNATING', parentA, parentB };
          break;
        default:
          pattern = { type: 'ALTERNATING', parentA, parentB };
      }

      await api.createCustodySchedule(familyId, {
        dependentId,
        patternType,
        pattern,
        startDate,
        handoverTime: handoverTime || undefined,
        handoverLocation: handoverLocation || undefined,
        notes: notes || undefined,
      });

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to save custody schedule');
    } finally {
      setLoading(false);
    }
  };

  const parents = familyMembers.filter((m) => m.role === 'OWNER' || m.role === 'MEMBER');

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <FormField label="Schedule type" id="custody-pattern-type" required>
        <select
          id="custody-pattern-type"
          value={patternType}
          onChange={(e) => setPatternType(e.target.value as CustodyPatternType)}
          className="input"
        >
          <option value="ALTERNATING">Alternating weeks</option>
          <option value="WEEKLY">Fixed weekly pattern</option>
        </select>
      </FormField>

      {patternType === 'ALTERNATING' && (
        <>
          <FormField label="Parent A (starts first)" id="custody-parent-a" required>
            <select
              id="custody-parent-a"
              value={parentA}
              onChange={(e) => setParentA(e.target.value)}
              className="input"
            >
              {parents.map(({ user }) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Parent B" id="custody-parent-b" required>
            <select
              id="custody-parent-b"
              value={parentB}
              onChange={(e) => setParentB(e.target.value)}
              className="input"
            >
              {parents.map(({ user }) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </FormField>
        </>
      )}

      {patternType === 'WEEKLY' && (
        <div className="mb-6">
          <label className="label">Weekly assignments</label>
          <div className="space-y-2 mt-2">
            {DAYS.map((day) => (
              <div key={day} className="flex items-center gap-3">
                <span className="w-10 text-sm font-medium text-gray-700">{DAY_LABELS[day]}</span>
                <select
                  value={weeklyAssignments[day]}
                  onChange={(e) =>
                    setWeeklyAssignments((prev) => ({ ...prev, [day]: e.target.value }))
                  }
                  className="input flex-1"
                >
                  {parents.map(({ user }) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      <FormField label="Start date" id="custody-start-date" required>
        <input
          type="date"
          id="custody-start-date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="input"
          required
        />
      </FormField>

      <FormField label="Handover time" id="custody-handover-time">
        <input
          type="time"
          id="custody-handover-time"
          value={handoverTime}
          onChange={(e) => setHandoverTime(e.target.value)}
          className="input"
        />
      </FormField>

      <FormField label="Handover location" id="custody-handover-location">
        <input
          type="text"
          id="custody-handover-location"
          value={handoverLocation}
          onChange={(e) => setHandoverLocation(e.target.value)}
          className="input"
          placeholder="e.g., School car park"
        />
      </FormField>

      <FormField label="Notes" id="custody-notes">
        <textarea
          id="custody-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="input"
          rows={2}
          placeholder="Any additional notes..."
        />
      </FormField>

      <div className="flex gap-3 mt-8">
        <button type="button" onClick={onCancel} className="flex-1 btn btn-secondary" disabled={loading}>
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save schedule'}
        </button>
      </div>
    </form>
  );
}
